import React from 'react';
import { Container, Typography, Box, Card, CardContent, Paper, Button, Avatar, Rating, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import VerifiedIcon from '@mui/icons-material/Verified';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const Home: React.FC = () => {
  const services = [
    {
      title: 'CMMI Services',
      description: 'CMMI Consultancy and Appraisal services to help organizations improve their processes.',
      icon: <BusinessIcon sx={{ fontSize: '2.5rem' }} />,
      color: '#1976d2',
    },
    {
      title: 'ISO Services',
      description: 'ISO 9001:2015, ISO 27001:2022, and ISO 20000-1:2018 certification services.',
      icon: <SecurityIcon sx={{ fontSize: '2.5rem' }} />,
      color: '#2e7d32',
    },
    {
      title: 'Digital Services',
      description: 'Digital Transformation, Software Development, and Quality Assurance services.',
      icon: <DevicesIcon sx={{ fontSize: '2.5rem' }} />,
      color: '#7b1fa2',
    },
    {
      title: 'Training',
      description: 'Professional training programs to enhance your team\'s capabilities.',
      icon: <SchoolIcon sx={{ fontSize: '2.5rem' }} />,
      color: '#e65100',
    },
  ];

  const features = [
    {
      title: 'Expert Team',
      description: 'Our team consists of certified professionals with years of industry experience.',
      icon: <GroupsIcon sx={{ fontSize: '3rem' }} />,
      color: '#1976d2',
    },
    {
      title: 'Quality Assurance',
      description: 'We maintain the highest standards of quality in all our services.',
      icon: <VerifiedIcon sx={{ fontSize: '3rem' }} />,
      color: '#2e7d32',
    },
    {
      title: 'Customer Focus',
      description: 'Your success is our priority. We work closely with you to achieve your goals.',
      icon: <HandshakeIcon sx={{ fontSize: '3rem' }} />,
      color: '#7b1fa2',
    },
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          pt: 15,
          pb: 15,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              component="h1"
              variant="h2"
              fontWeight="bold"
              gutterBottom
              sx={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
            >
              Right Track Technologies Limited
            </Typography>
            <Typography 
              variant="h5" 
              paragraph
              sx={{ mb: 5, maxWidth: '700px', lineHeight: 1.5 }}
            >
              Process-Driven Company delivering superior, sustained and ground-breaking solutions
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                size="large"
                component="a" 
                href="/contact"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  mt: 3,
                  py: 1.5,
                  px: 4,
                  borderRadius: '50px',
                  background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Get Started
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                component="a" 
                href="/services"
                sx={{
                  mt: 3,
                  py: 1.5,
                  px: 4,
                  borderRadius: '50px',
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Our Services
              </Button>
            </Box>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          transform: 'translate(30%, 30%)',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          top: 100,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      <Container maxWidth="lg">
        {/* Services Section */}
        <Box sx={{ py: 10 }}>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              textAlign="center"
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Our Services
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              textAlign="center" 
              sx={{ mb: 6, maxWidth: '700px', mx: 'auto', fontSize: '1.1rem' }}
            >
              We provide a wide range of IT and certification services to help your business reach new heights
            </Typography>
          </motion.div>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 4 }}>
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.12)',
                      '& .icon': {
                        transform: 'scale(1.2)',
                      }
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box sx={{ 
                    background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}99 100%)`,
                    p: 3,
                    color: 'white'
                  }}>
                    <Box 
                      className="icon"
                      sx={{ 
                        mb: 1,
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography gutterBottom variant="h5" component="h2" fontWeight="bold">
                      {service.title}
                    </Typography>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="body1">
                      {service.description}
                    </Typography>
                    <Button 
                      sx={{ 
                        mt: 2, 
                        textTransform: 'none', 
                        fontWeight: 'bold',
                        color: service.color,
                        '&:hover': { 
                          background: 'transparent',
                          '& .arrow': {
                            transform: 'translateX(5px)',
                          }
                        } 
                      }}
                      component="a"
                      href="/services"
                      color="primary"
                    >
                      Learn more <ArrowForwardIcon className="arrow" sx={{ ml: 1, fontSize: '0.9rem', transition: 'transform 0.2s ease' }}/>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Values Section with Background */}
      <Box sx={{ 
        py: 10, 
        background: 'linear-gradient(rgba(245, 247, 250, 0.95), rgba(245, 247, 250, 0.95)), url("https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              textAlign="center"
              fontWeight="bold"
              gutterBottom 
              sx={{ mb: 6 }}
            >
              Our Values
            </Typography>
          </motion.div>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            {[
              'Safe and Secure',
              'Fast and Reliable',
              'Trust and Integrity',
              'Seamless Integration',
              'Creativity and Innovation',
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ flex: '0 1 200px' }}
              >
                <Box sx={{ 
                  textAlign: 'center',
                  p: 3,
                  borderRadius: '12px',
                  background: 'white',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    transform: 'translateY(-5px)'
                  }
                }}>
                  <Typography 
                    variant="h6" 
                    fontWeight="medium"
                    color="primary"
                  >
                    {value}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Success Metrics Section - New */}
      <Box sx={{ 
        py: 10, 
        background: '#f5f7fa',
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              textAlign="center"
              fontWeight="bold"
              gutterBottom 
              sx={{ mb: 2 }}
            >
              Go-to-market success with a proven framework
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              textAlign="center" 
              sx={{ mb: 6, maxWidth: '700px', mx: 'auto', fontSize: '1.1rem' }}
            >
              We've tested our go-to-market framework, helping clients live up to their highest potential through our decades of experience.
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              md: 'repeat(3, 1fr)' 
            }, 
            gap: 4 
          }}>
            {[
              { label: 'Integrity', value: '95%', color: '#1976d2' },
              { label: 'Operational Excellence', value: '99%', color: '#2e7d32' },
              { label: 'Innovation', value: '95%', color: '#7b1fa2' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 5, 
                    textAlign: 'center',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.12)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color={stat.color} gutterBottom>
                    {stat.label}
                  </Typography>
                  <Typography 
                    variant="h2" 
                    component="p" 
                    fontWeight="bold" 
                    sx={{ 
                      color: stat.color, 
                      fontSize: { xs: '3rem', md: '4rem' },
                      my: 2
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* FAQ Section - New */}
      <Box sx={{ 
        py: 10, 
        background: '#f5f7fa',
      }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              textAlign="center"
              fontWeight="bold"
              gutterBottom 
              sx={{ mb: 2 }}
            >
              FAQ
            </Typography>
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              textAlign="center" 
              sx={{ mb: 6, maxWidth: '700px', mx: 'auto', fontSize: '1.1rem' }}
            >
              A few things you should know about us.
            </Typography>
          </motion.div>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {[
              { 
                question: 'What is CMMI certification?', 
                answer: 'CMMI (Capability Maturity Model Integration) is a process improvement framework that helps organizations streamline process improvement and encourage productive, efficient behaviors that decrease risks in software, product, and service development.' 
              },
              { 
                question: 'What services do you provide?', 
                answer: 'We provide a comprehensive range of services including CMMI services, ISO certification services, digital transformation, software development, quality assurance, data entry services, HR outsourcing, research & development, and managed services for telecom sector.' 
              },
              { 
                question: 'How can your services benefit my business?', 
                answer: 'Our services can help your business improve process efficiency, ensure quality standards compliance, enhance security measures, streamline operations, and accelerate digital transformation - all leading to improved customer satisfaction and competitive advantage.' 
              },
              { 
                question: 'Where are your offices located?', 
                answer: 'Head Office: 1 Norana Avenue, Favona, Auckland, postal code-2024, New Zealand and Regional Office: 43325 John Michael Ter, Ashburn, VA 20148, USA.' 
              },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Accordion 
                  elevation={0}
                  sx={{ 
                    mb: 2, 
                    borderRadius: '8px', 
                    '&:before': { display: 'none' },
                    boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      '& .MuiAccordionSummary-content': { 
                        margin: '16px 0',
                      },
                      '&.Mui-expanded': {
                        borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
                      }
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 2 }}>
                    <Typography variant="body1" color="text.secondary">
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <Button 
              variant="outlined" 
              color="primary"
              component="a" 
              href="/contact"
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: 3,
                py: 1.5,
                px: 4,
                borderRadius: '50px',
                '&:hover': {
                  background: 'rgba(25, 118, 210, 0.08)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              Get in Touch
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          py: 8, 
          px: 4,
          mb: 10,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative circles */}
        <Box sx={{
          position: 'absolute',
          top: -30,
          left: -30,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
          zIndex: 0
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -40,
          right: -40,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
          zIndex: 0
        }} />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Ready to transform your business?
          </Typography>
          <Typography sx={{ maxWidth: '700px', mx: 'auto', mb: 4, opacity: 0.9 }}>
            Contact us today to discuss how we can help you achieve your goals
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size="large"
              component="a" 
              href="/contact"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '50px',
                background: 'white',
                color: '#0c2340',
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Contact Us
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              component="a" 
              href="/services"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '50px',
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.1)',
                },
                transition: 'all 0.3s ease'
              }}
            >
              Our Services
            </Button>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Home; 